import React, { useState, useRef, useCallback } from 'react';
import {
  startImageEditJob,
  pollEditJob,
  mockEditImage,
  ImageEditRequest,
} from '../services/imageEditing';
import { models, Model } from '../data/models';
import { Asset } from '../services/assets';
import AssetPickerModal from '../components/AssetPickerModal';
import { API_URL } from '../config/apiConfig';

type EditStatus = 'idle' | 'processing' | 'completed' | 'failed';

export default function ImageEditingPage() {
  const [selectedModel, setSelectedModel] = useState<Model>(models[0]);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [prompt, setPrompt] = useState('');
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [status, setStatus] = useState<EditStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [editedImageUrl, setEditedImageUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Close model dropdown when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setIsModelDropdownOpen(false);
      }
    }

    if (isModelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModelDropdownOpen]);

  const handleImageSelect = useCallback((file: File) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      setSelectedAsset(null); // Clear asset when image is selected
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAsset(asset);
    setSelectedImage(null); // Clear uploaded image when asset is selected
    const assetUrl = asset.s3_url || asset.storage_url || '';
    setImagePreview(assetUrl);
  };

  const sourceImageUrl = imagePreview || (selectedAsset ? (selectedAsset.s3_url || selectedAsset.storage_url || null) : null);
  const canEdit = sourceImageUrl && prompt.trim().length >= 5 && selectedModel;

  const handleEdit = async () => {
    if (!canEdit || !sourceImageUrl) return;

    setStatus('processing');
    setProgress(0);
    setError(null);
    setEditedImageUrl(null);

    if (!API_URL) {
      // Mock mode - use Canvas-based editing
      try {
        const resultUrl = await mockEditImage(sourceImageUrl, (prog) => setProgress(prog));
        setEditedImageUrl(resultUrl);
        setStatus('completed');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to edit image');
        setStatus('failed');
      }
      return;
    }

    // Online mode - use real API
    try {
      const request: ImageEditRequest = {
        imageFile: selectedImage || undefined,
        assetUrl: selectedAsset ? (selectedAsset.s3_url || selectedAsset.storage_url || undefined) : undefined,
        prompt: prompt.trim(),
        model: selectedModel.id,
      };

      const response = await startImageEditJob(request);

      // Poll for status
      let pollCount = 0;
      const TEN_MINUTES_MS = 10 * 60 * 1000;
      const maxPolls = 120; // 10 minutes max (120 * 5 seconds)
      const startTime = Date.now();
      
      const pollInterval = setInterval(async () => {
        try {
          pollCount++;
          const jobStatus = await pollEditJob(response.jobId);
          setProgress(jobStatus.progress || 0);

          if (jobStatus.status === 'completed') {
            clearInterval(pollInterval);
            setEditedImageUrl(jobStatus.imageUrl || null);
            setStatus('completed');
          } else if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            setError(jobStatus.error || 'Editing failed');
            setStatus('failed');
          } else if (Date.now() - startTime >= TEN_MINUTES_MS) {
            clearInterval(pollInterval);
            setError('Image editing is taking too long. No result was generated after 10 minutes. Please try again.');
            setStatus('failed');
          } else if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setError('Image editing is taking longer than expected. Please try again.');
            setStatus('failed');
          }
        } catch (err) {
          if (pollCount >= 5) {
            clearInterval(pollInterval);
            setError(err instanceof Error ? err.message : 'Failed to check job status');
            setStatus('failed');
          }
        }
      }, 5000); // Poll every 5 seconds
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start editing');
      setStatus('failed');
    }
  };

  const handleReset = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedAsset(null);
    setPrompt('');
    setStatus('idle');
    setProgress(0);
    setEditedImageUrl(null);
    setError(null);
  };

  return (
    <div className="min-h-full bg-bg py-12">
      <div className="fluid-container">
        {/* Header */}
        <div className="mb-8">
          <h1 className="fluid-h2 font-bold text-white mb-4">Image Editing</h1>
          <p className="text-white/70 text-lg">
            Upload or select an image and describe how you want to edit it using AI.
          </p>
        </div>

        {/* Model Selector */}
        <div className="mb-6">
          <label className="block text-white text-lg font-medium mb-3">Model</label>
          <div className="relative" ref={modelDropdownRef}>
            <button
              onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
              className="w-full bg-black/50 border border-white/20 rounded-lg px-4 py-3 text-white flex items-center justify-between hover:border-white/40 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span>{selectedModel.name}</span>
                <span className="bg-white/10 text-white/70 text-xs px-2 py-1 rounded-full">
                  {selectedModel.credits} credits
                </span>
              </div>
              <svg
                className={`w-5 h-5 text-white/60 transition-transform ${
                  isModelDropdownOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isModelDropdownOpen && (
              <div className="absolute z-10 w-full mt-2 bg-bg border border-white/20 rounded-lg overflow-hidden">
                {models.map((model) => (
                  <button
                    key={model.id}
                    onClick={() => {
                      setSelectedModel(model);
                      setIsModelDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-white/10 transition-colors ${
                      selectedModel.id === model.id ? 'bg-white/10' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-white">{model.name}</span>
                      <span className="text-white/60 text-sm">{model.credits} credits</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Source Image Section */}
        <div className="mb-6">
          <label className="block text-white text-lg font-medium mb-3">Source Image</label>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Upload Image Card */}
            <div
              className={`border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
                isDragging
                  ? 'border-white bg-white/5'
                  : selectedImage
                  ? 'border-white bg-white/5'
                  : 'border-white/30 hover:border-white/50 bg-white/5'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
              />
              {selectedImage ? (
                <div className="flex flex-col items-center">
                  <img
                    src={imagePreview || ''}
                    alt="Preview"
                    className="w-full h-32 object-contain rounded-lg mb-2"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(null);
                      setImagePreview(null);
                    }}
                    className="text-white/70 hover:text-white text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 mb-3 flex items-center justify-center">
                    <svg
                      className="w-full h-full text-white/50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-white text-sm font-medium">Upload Image</p>
                </div>
              )}
            </div>

            {/* Select from Assets Card */}
            <div
              className={`border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${
                selectedAsset
                  ? 'border-white bg-white/5'
                  : 'border-white/30 hover:border-white/50 bg-white/5'
              }`}
              onClick={() => setIsAssetModalOpen(true)}
            >
              {selectedAsset ? (
                <div className="flex flex-col items-center">
                  <img
                    src={selectedAsset.s3_url || selectedAsset.storage_url || ''}
                    alt={selectedAsset.prompt || selectedAsset.operation_type || 'Asset'}
                    className="w-full h-32 object-contain rounded-lg mb-2"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EAsset%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <p className="text-white text-xs mb-1">
                    {selectedAsset.prompt 
                      ? (selectedAsset.prompt.length > 30 ? selectedAsset.prompt.substring(0, 30) + '...' : selectedAsset.prompt)
                      : selectedAsset.operation_type || 'Asset'}
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedAsset(null);
                      if (selectedImage) {
                        setImagePreview(null);
                      }
                    }}
                    className="text-white/70 hover:text-white text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 mb-3 flex items-center justify-center">
                    <svg
                      className="w-full h-full text-white/50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-white text-sm font-medium">Select from assets</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Editing Instructions */}
        <div className="mb-8">
          <label className="block text-white text-lg font-medium mb-3">
            Editing Instructions
          </label>
          <div className="relative">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe how you want to edit the image. For example: Change the background to a sunset beach scene, or Add sunglasses to the person."
              className="w-full bg-black/50 border border-white/20 rounded-lg p-4 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 resize-none"
              rows={6}
              maxLength={500}
            />
            <div className="absolute bottom-3 right-3 text-white/50 text-sm">
              {prompt.length}/500
            </div>
          </div>
        </div>

        {/* Edit Button */}
        <div className="flex flex-col items-center mb-8">
          <button
            onClick={handleEdit}
            disabled={!canEdit || status === 'processing'}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold px-12 py-4 rounded-full transition-colors text-lg"
          >
            {status === 'processing' ? 'Editing...' : 'Edit Image'}
          </button>
          <div className="mt-3 flex items-center gap-4 text-white/60 text-sm">
            <span>Est. 30 seconds</span>
            <span className="flex items-center gap-1">
              <span>{selectedModel.credits}</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                  clipRule="evenodd"
                />
              </svg>
              credits
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        {status === 'processing' && (
          <div className="mb-8">
            <div className="w-full bg-white/10 rounded-full h-2 mb-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-white/60 text-sm text-center">{Math.round(progress)}%</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {error}
          </div>
        )}


        {/* Result Image */}
        {status === 'completed' && editedImageUrl && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Result</h2>
            <div className="bg-white/5 border border-white/20 rounded-xl p-6 mb-4">
              <img
                src={editedImageUrl}
                alt="Edited"
                className="w-full rounded-lg"
                onError={() => {
                  setError('Failed to load edited image.');
                }}
              />
            </div>
            <div className="flex gap-4 justify-center">
              <a
                href={`${API_URL}/api/assets/download-file?file_url=${encodeURIComponent(editedImageUrl)}&filename=edited-image.png`}
                download="edited-image.png"
                className="bg-[#F9F7EA] text-[#0f1218] px-6 py-2 rounded-lg font-semibold hover:bg-[#E7D7B1] transition-colors"
              >
                Download
              </a>
              <button
                onClick={handleReset}
                className="bg-white/10 text-white px-6 py-2 rounded-lg font-semibold hover:bg-white/20 transition-colors"
              >
                Edit Another
              </button>
            </div>
          </div>
        )}

        {/* Asset Picker Modal */}
        <AssetPickerModal
          isOpen={isAssetModalOpen}
          onClose={() => setIsAssetModalOpen(false)}
          onSelect={handleAssetSelect}
          selectedAssetId={selectedAsset?.id}
        />
      </div>
    </div>
  );
}

